import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, formatRelative } from '../lib/api';
import { Alert, Button, Card, Empty, Field, Input, Modal, Select, Spinner } from '../components/ui';

/**
 * Intercom: who can talk to whom.
 *
 * The intercom is its own service with its own people - crew and freelancers
 * who never sign in to the hub. This page is where they are made, where
 * channels are made, and where each person is put on channels. Changes take
 * effect at once, for people who are connected too.
 *
 * A "programme source" is a sign-in for a computer, not a person: it feeds
 * programme audio and a video return into channels.
 */

interface IUser { id: string; username: string; name: string; role: 'user' | 'pgm'; active: boolean; lastSeenAt: string | null }
interface IChannel { id: string; name: string; colour: string; sort: number }
interface IMember { userId: string; channelId: string; talk: boolean; listen: boolean }
interface ILive {
  userId: string; name: string; role: string; since: number; ip: string; userAgent: string; connected: boolean; joined: boolean;
  talk: string[]; listen: string[]; audioChannels: string[]; videoChannels: string[]; sending: string[]; receiving: number;
  transport: string | null; health: { quality: string; rttMs: number; lossPct: number; at: number } | null;
}
interface State {
  server: { startedAt: number; online: number; rtcPort: number };
  clientUrl: string;
  channels: IChannel[];
  users: IUser[];
  memberships: IMember[];
  live: ILive[];
}

const COLOURS = ['#e5484d', '#e0a02b', '#34c26e', '#3987e5', '#a56eff', '#e667af', '#64748b'];

/** Easy to read out and type on a phone: no look-alike characters. */
function suggestPassword(): string {
  const set = 'abcdefghjkmnpqrstuvwxyz23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  return Array.from(bytes, (b) => set[b % set.length]).join('');
}

const QUALITY: Record<string, string> = { good: 'var(--color-good)', fair: 'var(--color-warning)', poor: 'var(--color-critical)' };

export function Intercom() {
  const [state, setState] = useState<State | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addUser, setAddUser] = useState<'user' | 'pgm' | null>(null);
  const [editUser, setEditUser] = useState<IUser | null>(null);
  const [editChannel, setEditChannel] = useState<IChannel | 'new' | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      setState(await api.get<State>('/intercom/state'));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the intercom.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 3000);
    return () => clearInterval(timer);
  }, [load]);

  async function act(call: () => Promise<unknown>): Promise<void> {
    try {
      await call();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    }
  }

  if (loading) return <Spinner label="Loading the intercom…" />;
  if (!state) return <Alert tone="critical" title="The intercom is not answering">{error}</Alert>;

  const { channels, users, memberships, live } = state;
  const right = (userId: string, channelId: string) => memberships.find((m) => m.userId === userId && m.channelId === channelId);
  const liveOf = (userId: string) => live.find((p) => p.userId === userId);
  const people = users.filter((u) => u.role === 'user');
  const sources = users.filter((u) => u.role === 'pgm');

  /** A person's cell goes round: talk and listen -> listen only -> not on it. A source's is on or off. */
  function cycle(u: IUser, c: IChannel): void {
    const r = right(u.id, c.id);
    const body = u.role === 'pgm'
      ? (r ? { member: false } : { talk: true, listen: true })
      : !r ? { talk: true, listen: true } : r.talk ? { talk: false, listen: true } : { member: false };
    void act(() => api.put('/intercom/memberships', { userId: u.id, channelId: c.id, ...body }));
  }

  const grid = (rows: IUser[], title: string, empty: string) => (
    <Card title={title}>
      {rows.length === 0 ? <Empty title={empty} /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-grid text-left text-xs text-ink-muted">
                <th className="sticky left-0 z-10 bg-surface px-4 py-2 font-medium">Name</th>
                {channels.map((c) => (
                  <th key={c.id} className="px-2 py-2 text-center font-medium">
                    <button className="hover:underline" style={{ color: c.colour }} onClick={() => setEditChannel(c)} title="Rename, recolour or delete">
                      {c.name}
                    </button>
                  </th>
                ))}
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => {
                const on = liveOf(u.id);
                return (
                  <tr key={u.id} className="border-b border-grid last:border-0 hover:bg-raised/40">
                    <td className="sticky left-0 z-10 bg-surface px-4 py-2">
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          title={on ? 'Connected' : 'Not connected'}
                          style={{ background: on ? 'var(--color-good)' : 'var(--color-ink-muted)' }}
                        />
                        <span className={`font-medium ${u.active ? '' : 'line-through opacity-50'}`}>{u.name}</span>
                      </div>
                      <div className="pl-4 text-[11px] text-ink-muted">
                        signs in as <span className="font-mono">{u.username}</span>
                        {!u.active && ' · switched off'}
                        {!on && u.lastSeenAt && ` · last on ${formatRelative(u.lastSeenAt)}`}
                      </div>
                    </td>
                    {channels.map((c) => {
                      const r = right(u.id, c.id);
                      const talking = on?.talk.includes(c.id) || (on?.audioChannels.includes(c.id) && on.sending.includes('pgm-audio'));
                      const label = !r ? '–' : u.role === 'pgm' ? 'Feeds' : r.talk ? 'Talk + listen' : 'Listen';
                      return (
                        <td key={c.id} className="px-2 py-2 text-center">
                          <button
                            onClick={() => cycle(u, c)}
                            title="Click to change"
                            className={`min-w-[92px] rounded px-2 py-1 text-xs ring-1 ring-hairline ${r ? 'text-ink' : 'text-ink-muted'}`}
                            style={r ? { background: `color-mix(in srgb, ${c.colour} ${talking ? 55 : r.talk ? 22 : 10}%, transparent)` } : undefined}
                          >
                            {label}{talking && ' ●'}
                          </button>
                        </td>
                      );
                    })}
                    <td className="px-4 py-2">
                      <div className="flex justify-end gap-1">
                        {on && <Button variant="ghost" onClick={() => void act(() => api.post(`/intercom/users/${u.id}/kick`))}>Disconnect</Button>}
                        {channels.length > 0 && (
                          <Button
                            variant="ghost"
                            title="Put them on every channel"
                            onClick={() => void act(() => api.put(`/intercom/users/${u.id}/channels`, { channels: channels.map((c) => ({ channelId: c.id })) }))}
                          >
                            All
                          </Button>
                        )}
                        {memberships.some((m) => m.userId === u.id) && (
                          <Button
                            variant="ghost"
                            title="Take them off every channel"
                            onClick={() => void act(() => api.put(`/intercom/users/${u.id}/channels`, { channels: [] }))}
                          >
                            Clear
                          </Button>
                        )}
                        <Button variant="ghost" onClick={() => setEditUser(u)}>Edit</Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Intercom</h1>
          <p className="text-sm text-ink-muted">
            People open{' '}
            <a className="underline" href={state.clientUrl} target="_blank" rel="noreferrer">{state.clientUrl.replace(/^https?:\/\//, '')}</a>
            {' '}on a phone or computer and sign in with the name and password made here. {live.length} connected now.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setEditChannel('new')}>Add channel</Button>
          <Button onClick={() => setAddUser('pgm')}>Add programme source</Button>
          <Button variant="primary" onClick={() => setAddUser('user')}>Add person</Button>
        </div>
      </div>

      {error && <Alert tone="critical">{error}</Alert>}
      {channels.length === 0 && (
        <Alert tone="info" title="Start with a channel">
          A channel is a group that can hear each other: Production, Cameras, Floor. Add one, then add people.
        </Alert>
      )}

      {grid(people, 'People', 'Nobody yet')}
      {grid(sources, 'Programme sources', 'None yet. A source is a computer that feeds programme audio or a video return into channels.')}

      <Card title={`Connected now (${live.length})`}>
        {live.length === 0 ? <Empty title="Nobody is connected" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-grid text-left text-xs text-ink-muted">
                  <th className="px-4 py-2 font-medium">Who</th>
                  <th className="px-4 py-2 font-medium">Connection</th>
                  <th className="px-4 py-2 font-medium">Doing</th>
                  <th className="px-4 py-2 font-medium">From</th>
                  <th className="px-4 py-2 font-medium">Since</th>
                </tr>
              </thead>
              <tbody>
                {live.map((p) => {
                  const names = (ids: string[]) => ids.map((id) => channels.find((c) => c.id === id)?.name ?? '?').join(', ');
                  const doing = p.role === 'pgm'
                    ? [p.sending.includes('pgm-audio') && `audio to ${names(p.audioChannels) || 'no channels'}`, p.sending.includes('pgm-video') && `video to ${names(p.videoChannels) || 'no channels'}`].filter(Boolean).join(' · ') || 'idle'
                    : p.talk.length ? `talking on ${names(p.talk)}` : `listening to ${p.listen.length}`;
                  return (
                    <tr key={p.userId} className="border-b border-grid last:border-0">
                      <td className="px-4 py-2 font-medium">{p.name}</td>
                      <td className="px-4 py-2 text-xs">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full" style={{ background: !p.connected ? 'var(--color-warning)' : QUALITY[p.health?.quality ?? ''] ?? 'var(--color-ink-muted)' }} />
                          {!p.connected ? 'reconnecting' : p.health ? `${p.health.quality} · ${p.health.rttMs} ms · ${p.health.lossPct}% loss` : 'connected'}
                          {p.transport && ` · ${p.transport.toUpperCase()}`}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-ink-secondary">{doing}</td>
                      <td className="px-4 py-2 text-xs text-ink-secondary" title={p.userAgent}>{p.ip}</td>
                      <td className="px-4 py-2 text-xs text-ink-secondary">{formatRelative(new Date(p.since).toISOString())}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {addUser && <UserModal role={addUser} onClose={() => setAddUser(null)} onDone={() => { setAddUser(null); void load(); }} />}
      {editUser && <UserModal user={editUser} role={editUser.role} onClose={() => setEditUser(null)} onDone={() => { setEditUser(null); void load(); }} />}
      {editChannel && (
        <ChannelModal
          channel={editChannel === 'new' ? null : editChannel}
          nextColour={COLOURS[channels.length % COLOURS.length]}
          onClose={() => setEditChannel(null)}
          onDone={() => { setEditChannel(null); void load(); }}
        />
      )}
    </div>
  );
}

function UserModal({ user, role, onClose, onDone }: { user?: IUser; role: 'user' | 'pgm'; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(user?.name ?? '');
  const [username, setUsername] = useState(user?.username ?? '');
  const [password, setPassword] = useState(user ? '' : suggestPassword());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const what = role === 'pgm' ? 'programme source' : 'person';

  async function run(call: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await call();
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={user ? `Edit ${user.name}` : `Add a ${what}`}
      footer={
        <>
          {user && (
            <>
              <Button variant="ghost" className="mr-auto" disabled={busy} onClick={() => void run(() => api.delete(`/intercom/users/${user.id}`))}>Delete</Button>
              <Button disabled={busy} onClick={() => void run(() => api.patch(`/intercom/users/${user.id}`, { active: !user.active }))}>
                {user.active ? 'Switch off' : 'Switch back on'}
              </Button>
            </>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!name.trim() || (!user && (!username.trim() || password.length < 6))}
            onClick={() => void run(() => user
              ? api.patch(`/intercom/users/${user.id}`, { name, ...(password ? { password } : {}) })
              : api.post('/intercom/users', { name, username, password, role }))}
          >
            {user ? 'Save' : 'Add'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <Alert tone="critical">{error}</Alert>}
        <Field label="Name" hint="What other people see: who is talking, who is online.">
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder={role === 'pgm' ? 'Programme' : 'Sam (Camera 2)'} />
        </Field>
        <Field label="Signs in as" hint={user ? 'Cannot be changed.' : 'Short, no spaces. Capitals do not matter.'}>
          <Input value={username} disabled={!!user} onChange={(e) => setUsername(e.target.value.replace(/\s/g, ''))} maxLength={40} placeholder={role === 'pgm' ? 'pgm' : 'sam'} autoCapitalize="none" />
        </Field>
        <Field
          label={user ? 'New password' : 'Password'}
          hint={user ? 'Leave empty to keep the one they have. Changing it signs them out everywhere.' : 'Give this to them. It is not shown again.'}
        >
          <div className="flex gap-2">
            <Input value={password} onChange={(e) => setPassword(e.target.value)} className="font-mono" autoComplete="off" />
            <Button onClick={() => setPassword(suggestPassword())}>New</Button>
          </div>
        </Field>
        {!user && <p className="text-xs text-ink-muted">They start on every channel. Change that in the grid afterwards.</p>}
      </div>
    </Modal>
  );
}

function ChannelModal({ channel, nextColour, onClose, onDone }: { channel: IChannel | null; nextColour: string; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(channel?.name ?? '');
  const [colour, setColour] = useState(channel?.colour ?? nextColour);
  const [sort, setSort] = useState(String(channel?.sort ?? ''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(call: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await call();
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={channel ? `Edit ${channel.name}` : 'Add a channel'}
      footer={
        <>
          {channel && <Button variant="ghost" className="mr-auto" disabled={busy} onClick={() => void run(() => api.delete(`/intercom/channels/${channel.id}`))}>Delete</Button>}
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!name.trim()}
            onClick={() => void run(() => channel
              ? api.patch(`/intercom/channels/${channel.id}`, { name, colour, ...(sort.trim() ? { sort: Number(sort) } : {}) })
              : api.post('/intercom/channels', { name, colour }))}
          >
            {channel ? 'Save' : 'Add'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <Alert tone="critical">{error}</Alert>}
        <Field label="Name">
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={40} placeholder="Production" />
        </Field>
        <Field label="Colour">
          <Select value={colour} onChange={(e) => setColour(e.target.value)}>
            {[...new Set([colour, ...COLOURS])].map((c) => <option key={c} value={c} style={{ color: c }}>● {c}</option>)}
          </Select>
        </Field>
        {channel && (
          <Field label="Position" hint="Lower numbers are higher up everyone's list.">
            <Input value={sort} onChange={(e) => setSort(e.target.value.replace(/[^\d-]/g, ''))} />
          </Field>
        )}
        {!channel && <p className="text-xs text-ink-muted">Everyone already on the intercom is put on a new channel. Change that in the grid afterwards.</p>}
        {channel && <p className="text-xs text-ink-muted">Deleting a channel takes it off everyone's screen at once.</p>}
      </div>
    </Modal>
  );
}
